import { useState, useEffect } from "react";
import { T, PRINCIPLE_DEFAULTS } from "../../config/theme.js";
import { Btn, Input, Card } from "../ui/index.jsx";
import { api } from "../../hooks/useApi.js";
import { Settings, Save, X, AlertCircle } from "lucide-react";

export default function PrincipleSettings({ holding, token, onSave, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // 원칙 필드
  const [lossLimit, setLossLimit] = useState("");
  const [targetProfit, setTargetProfit] = useState("");
  const [maxHoldDays, setMaxHoldDays] = useState("");
  const [maxVolatility, setMaxVolatility] = useState("");
  const [disclosureSensitivity, setDisclosureSensitivity] = useState(true);
  const [newsSensitivity, setNewsSensitivity] = useState(true);
  const [memo, setMemo] = useState("");

  // 기존 원칙 불러오기
  useEffect(() => {
    if (!holding?.symbol) return;
    setLoading(true);
    api(`/principles/${holding.symbol}`, {}, token)
      .then(data => {
        const p = data?.principle;
        if (p) {
          setLossLimit(p.loss_limit_pct != null ? String(p.loss_limit_pct) : "");
          setTargetProfit(p.target_profit_pct != null ? String(p.target_profit_pct) : "");
          setMaxHoldDays(p.max_hold_days != null ? String(p.max_hold_days) : "");
          setMaxVolatility(p.max_volatility_pct != null ? String(p.max_volatility_pct) : "");
          setDisclosureSensitivity(p.disclosure_sensitivity ?? true);
          setNewsSensitivity(p.news_sensitivity ?? true);
          setMemo(p.investment_memo || "");
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [holding?.symbol, token]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        stock_code: holding.symbol,
        stock_name: holding.name,
        holding_id: holding.id,
        loss_limit_pct: lossLimit ? parseFloat(lossLimit) : null,
        target_profit_pct: targetProfit ? parseFloat(targetProfit) : null,
        max_hold_days: maxHoldDays ? parseInt(maxHoldDays) : null,
        max_volatility_pct: maxVolatility ? parseFloat(maxVolatility) : null,
        disclosure_sensitivity: disclosureSensitivity,
        news_sensitivity: newsSensitivity,
        investment_memo: memo || null,
      };

      const result = await api("/principles", {
        method: "POST",
        body: JSON.stringify(body),
      }, token);

      onSave?.(result.principle);
      onClose?.();
    } catch (e) {
      setError(e.message || "저장에 실패했습니다");
    }
    setSaving(false);
  };

  const applyDefaults = () => {
    setLossLimit(String(PRINCIPLE_DEFAULTS.loss_limit_pct));
    setTargetProfit(String(PRINCIPLE_DEFAULTS.target_profit_pct));
    setMaxHoldDays(String(PRINCIPLE_DEFAULTS.max_hold_days));
    setMaxVolatility(String(PRINCIPLE_DEFAULTS.max_volatility_pct));
  };

  if (loading) {
    return (
      <div style={overlayStyle}>
        <div style={modalStyle}>
          <div style={{ textAlign: "center", padding: 40, color: T.textDim }}>불러오는 중...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        {/* 헤더 */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Settings size={20} color={T.accent} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{holding.name} 투자 원칙</div>
              <div style={{ fontSize: 12, color: T.textDim }}>설정한 기준과 현재 데이터를 비교합니다</div>
            </div>
          </div>
          <span onClick={onClose} style={{ cursor: "pointer", padding: 4, color: T.textMuted }}>
            <X size={20} />
          </span>
        </div>

        {/* 기본값 적용 버튼 */}
        <div style={{ marginBottom: 20 }}>
          <Btn variant="ghost" style={{ fontSize: 12, padding: "6px 14px" }} onClick={applyDefaults}>
            기본값 적용 (손절 -8% / 목표 +15% / 보유 90일)
          </Btn>
        </div>

        {/* 원칙 입력 필드 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* 손실 허용 범위 */}
          <div>
            <div style={labelStyle}>손실 허용 범위 (%)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input
                type="number"
                placeholder="-8"
                value={lossLimit}
                onChange={e => setLossLimit(e.target.value)}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 13, color: T.textDim, whiteSpace: "nowrap" }}>% 이하 시 알림</span>
            </div>
            <div style={hintStyle}>예: -8 입력 시, 수익률이 -8% 이하로 떨어지면 이탈로 표시</div>
          </div>

          {/* 목표 수익률 */}
          <div>
            <div style={labelStyle}>목표 수익률 (%)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input
                type="number"
                placeholder="15"
                value={targetProfit}
                onChange={e => setTargetProfit(e.target.value)}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 13, color: T.textDim, whiteSpace: "nowrap" }}>% 도달 시 알림</span>
            </div>
          </div>

          {/* 최대 보유 기간 */}
          <div>
            <div style={labelStyle}>최대 보유 기간 (일)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input
                type="number"
                placeholder="90"
                value={maxHoldDays}
                onChange={e => setMaxHoldDays(e.target.value)}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 13, color: T.textDim, whiteSpace: "nowrap" }}>일 초과 시 알림</span>
            </div>
          </div>

          {/* 변동성 허용 범위 */}
          <div>
            <div style={labelStyle}>변동성 허용 범위 (%)</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Input
                type="number"
                placeholder="12"
                value={maxVolatility}
                onChange={e => setMaxVolatility(e.target.value)}
                style={{ flex: 1 }}
              />
              <span style={{ fontSize: 13, color: T.textDim, whiteSpace: "nowrap" }}>% 초과 시 알림</span>
            </div>
          </div>

          {/* 공시/뉴스 민감도 토글 */}
          <div style={{ display: "flex", gap: 20 }}>
            <label style={toggleStyle}>
              <input
                type="checkbox"
                checked={disclosureSensitivity}
                onChange={e => setDisclosureSensitivity(e.target.checked)}
                style={{ accentColor: T.accent }}
              />
              <span>공시 발생 시 점검 알림</span>
            </label>
            <label style={toggleStyle}>
              <input
                type="checkbox"
                checked={newsSensitivity}
                onChange={e => setNewsSensitivity(e.target.checked)}
                style={{ accentColor: T.accent }}
              />
              <span>부정 뉴스 급증 시 점검 알림</span>
            </label>
          </div>

          {/* 투자 메모 */}
          <div>
            <div style={labelStyle}>투자 메모 (선택)</div>
            <textarea
              placeholder="예: 실적 성장 확인 전까지 추가매수 안 함"
              value={memo}
              onChange={e => setMemo(e.target.value)}
              rows={3}
              style={{
                width: "100%",
                background: T.card,
                color: T.text,
                border: `1px solid ${T.border}`,
                padding: "12px 16px",
                borderRadius: 10,
                fontSize: 14,
                fontFamily: "inherit",
                resize: "vertical",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            <div style={hintStyle}>이 메모는 리포트에서 현재 데이터와 나란히 표시됩니다</div>
          </div>
        </div>

        {/* 에러 */}
        {error && (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16, color: T.red, fontSize: 13 }}>
            <AlertCircle size={14} /> {error}
          </div>
        )}

        {/* 면책 고지 */}
        <div style={{ marginTop: 20, padding: "10px 14px", background: T.bg, borderRadius: 8, fontSize: 11, color: T.textMuted, lineHeight: 1.6 }}>
          투자 원칙은 사용자가 직접 설정하는 기준이며, Pause는 이 기준의 적절성을 평가하지 않습니다. 설정한 기준과 현재 데이터의 비교 결과만 제공되며, 투자 판단은 본인의 책임입니다.
        </div>

        {/* 저장 버튼 */}
        <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
          <Btn variant="ghost" style={{ flex: 1, justifyContent: "center", padding: 14 }} onClick={onClose}>
            취소
          </Btn>
          <Btn variant="primary" style={{ flex: 2, justifyContent: "center", padding: 14 }} onClick={handleSave} disabled={saving}>
            <Save size={16} /> {saving ? "저장 중..." : "원칙 저장"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// 스타일 상수
const overlayStyle = {
  position: "fixed", inset: 0, zIndex: 10000,
  background: "rgba(5, 5, 10, 0.85)", backdropFilter: "blur(8px)",
  display: "flex", alignItems: "center", justifyContent: "center",
  padding: 20,
};

const modalStyle = {
  background: T.card, borderRadius: 20, border: `1px solid ${T.border}`,
  boxShadow: "0 24px 48px rgba(0,0,0,0.4)",
  maxWidth: 480, width: "100%", padding: "28px 24px",
  maxHeight: "90vh", overflowY: "auto",
};

const labelStyle = {
  fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 6,
};

const hintStyle = {
  fontSize: 11, color: T.textMuted, marginTop: 4,
};

const toggleStyle = {
  display: "flex", alignItems: "center", gap: 8,
  fontSize: 13, color: T.textDim, cursor: "pointer",
};
